// Activity Logs — admin-only page that surfaces the full audit trail across
// every user. Backed by the existing GET /api/admin/activity endpoint.
//
// Features:
//   - Tab toggle: User Activity (CRUD) vs System (auth/db/sysadmin)
//   - Filters: user, action substring, target_type, since/until date
//   - Paginated table with severity colour coding
//   - Per-row expand to show the JSON details payload

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MdHistory, MdRefresh, MdFilterAlt, MdClear, MdExpandMore, MdExpandLess,
  MdAdminPanelSettings, MdPersonOutline, MdSearch,
} from 'react-icons/md';
import api from '../api/axios';
import ExportButtons from '../components/ExportButtons';
import SearchableSelect from '../components/SearchableSelect';

const SEVERITY_COLOR = {
  danger:  { bg: '#ffebee', fg: '#c62828', border: '#ef9a9a' },
  warning: { bg: '#fff3e0', fg: '#e65100', border: '#ffcc80' },
  success: { bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  info:    { bg: '#e3f2fd', fg: '#1565c0', border: '#90caf9' },
  neutral: { bg: '#f5f5f5', fg: '#616161', border: '#e0e0e0' },
};

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString([], {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
}) : '—';

const SeverityBadge = ({ severity, action }) => {
  const c = SEVERITY_COLOR[severity] || SEVERITY_COLOR.neutral;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
      fontFamily: 'monospace', border: `1px solid ${c.border}`,
    }}>
      {action || '—'}
    </span>
  );
};

const StatusCode = ({ code }) => {
  if (!code) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = code >= 500 ? 'var(--danger)' :
                code >= 400 ? 'var(--warning)' :
                code >= 200 ? 'var(--success)' : 'var(--text-muted)';
  return <span style={{ color, fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>{code}</span>;
};

const ActivityLogs = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [category, setCategory] = useState('activity'); // 'activity' | 'system'
  const [filters, setFilters] = useState({
    user_id: '', action: '', target_type: '', since: '', until: '',
  });
  const [pendingSearch, setPendingSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(new Set());

  const TARGET_TYPES = ['', 'assets', 'locations', 'audits', 'audit_items',
    'asset_movements', 'users', 'readers', 'categories', 'movement_reasons',
    'audit_schedules', 'report_schedules', 'app_settings', 'raw_inventory'];

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { limit, offset, category };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/admin/activity', { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, category, filters]);

  useEffect(() => { load(); }, [load]);

  // Fetch user roster once for the user filter
  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const onSearch = () => {
    setFilters(f => ({ ...f, action: pendingSearch }));
    setOffset(0);
  };

  const onClearFilters = () => {
    setFilters({ user_id: '', action: '', target_type: '', since: '', until: '' });
    setPendingSearch('');
    setOffset(0);
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const hasFilters = useMemo(() =>
    Object.values(filters).some(Boolean) || pendingSearch,
    [filters, pendingSearch]
  );

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdHistory /> Activity Logs</div>
          <div className="page-subtitle">Audit trail of every action taken across the system</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ExportButtons
            rows={rows}
            filename={`activity-${category}-${new Date().toISOString().slice(0,10)}`}
            title={`Activity Log — ${category === 'system' ? 'System' : 'User Activity'}`}
            subtitle={`${total} total entries`}
            columns={[
              { key: 'created_at', label: 'When',     format: v => v ? new Date(v).toLocaleString() : '' },
              { key: 'username',   label: 'User',     format: v => v || 'system' },
              { key: 'action',     label: 'Action' },
              { key: 'summary',    label: 'Summary' },
              { key: 'method',     label: 'Method' },
              { key: 'path',       label: 'Path' },
              { key: 'status_code',label: 'Status' },
              { key: 'ip',         label: 'IP' },
              { key: 'target_type',label: 'Target' },
              { key: 'target_id',  label: 'ID' },
            ]}
          />
          <button className="btn btn-outline" onClick={load} disabled={loading}>
            <MdRefresh /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[
          { v: 'activity', label: 'User Activity', icon: <MdPersonOutline /> },
          { v: 'system',   label: 'System',        icon: <MdAdminPanelSettings /> },
        ].map(t => (
          <button key={t.v}
            onClick={() => { setCategory(t.v); setOffset(0); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px', background: 'transparent',
              border: 'none', borderBottom: category === t.v ? '2px solid var(--accent)' : '2px solid transparent',
              color: category === t.v ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row">
          <div className="search-bar" style={{ flex: '1 1 240px', minWidth: 200 }}>
            <MdSearch />
            <input placeholder="Search action (e.g. post.assets, delete)…"
              value={pendingSearch}
              onChange={e => setPendingSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSearch()} />
          </div>
          <SearchableSelect className="form-control" style={{ flex: '0 0 auto', width: 180 }}
            value={filters.user_id}
            onChange={e => { setFilters(f => ({ ...f, user_id: e.target.value })); setOffset(0); }}>
            <option value="">All users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.username}{u.full_name ? ` — ${u.full_name}` : ''}</option>
            ))}
          </SearchableSelect>
          <SearchableSelect className="form-control" style={{ flex: '0 0 auto', width: 180 }}
            value={filters.target_type}
            onChange={e => { setFilters(f => ({ ...f, target_type: e.target.value })); setOffset(0); }}>
            <option value="">All target types</option>
            {TARGET_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
          </SearchableSelect>
          <input type="datetime-local" className="form-control" style={{ flex: '0 0 auto', width: 200 }}
            value={filters.since}
            onChange={e => { setFilters(f => ({ ...f, since: e.target.value })); setOffset(0); }}
            placeholder="Since" title="Since" />
          <input type="datetime-local" className="form-control" style={{ flex: '0 0 auto', width: 200 }}
            value={filters.until}
            onChange={e => { setFilters(f => ({ ...f, until: e.target.value })); setOffset(0); }}
            placeholder="Until" title="Until" />
          <button className="btn btn-primary" onClick={onSearch}>
            <MdFilterAlt /> Apply
          </button>
          {hasFilters && (
            <button className="btn btn-outline" onClick={onClearFilters}>
              <MdClear /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>⚠ {error}</div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            {loading ? 'Loading…' : `${total.toLocaleString()} entries`}
            {hasFilters && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>(filtered)</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Page {currentPage} of {totalPages}
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th style={{ width: 160 }}>When</th>
                <th>What happened</th>
                <th style={{ width: 110 }}>User</th>
                <th style={{ width: 200 }}>Action</th>
                <th style={{ width: 60, textAlign: 'right' }}>Status</th>
                <th style={{ width: 100 }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="empty-state">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="empty-state">No activity matches your filters</td></tr>
              ) : rows.map(r => {
                const isOpen = expanded.has(r.id);
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(r.id)}>
                      <td style={{ color: 'var(--text-muted)' }}>{isOpen ? <MdExpandLess /> : <MdExpandMore />}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {fmtDateTime(r.created_at)}
                      </td>
                      <td>{r.summary || `${r.method || ''} ${r.path || ''}`}</td>
                      <td>
                        {r.username ? (
                          <span style={{ fontWeight: 600 }}>{r.username}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>system</span>
                        )}
                      </td>
                      <td><SeverityBadge severity={r.severity} action={r.action} /></td>
                      <td style={{ textAlign: 'right' }}><StatusCode code={r.status_code} /></td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                        {r.ip || '—'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ background: '#fafbfd', padding: '12px 20px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '6px 16px', fontSize: 12 }}>
                            {r.method && <><div style={{ color: 'var(--text-muted)' }}>HTTP method</div><div style={{ fontFamily: 'monospace' }}>{r.method}</div></>}
                            {r.path   && <><div style={{ color: 'var(--text-muted)' }}>Path</div><div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.path}</div></>}
                            {r.target_type && <><div style={{ color: 'var(--text-muted)' }}>Target</div><div>{r.target_type}{r.target_id ? ` #${r.target_id}` : ''}</div></>}
                            {r.user_agent && <><div style={{ color: 'var(--text-muted)' }}>User agent</div><div style={{ wordBreak: 'break-all' }}>{r.user_agent}</div></>}
                            {r.details && (
                              <>
                                <div style={{ color: 'var(--text-muted)' }}>Details</div>
                                <pre style={{
                                  margin: 0, padding: 10, background: '#fff',
                                  border: '1px solid var(--border-light)', borderRadius: 6,
                                  fontSize: 11, overflow: 'auto', maxHeight: 240,
                                }}>{JSON.stringify(r.details, null, 2)}</pre>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {Math.min(offset + 1, total)} – {Math.min(offset + limit, total)} of {total.toLocaleString()}
          </div>
          <div className="pagination-controls">
            <button className="page-btn" disabled={offset === 0}
              onClick={() => setOffset(0)} title="First">«</button>
            <button className="page-btn" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))} title="Previous">‹</button>
            <span className="page-btn active">{currentPage}</span>
            <button className="page-btn" disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)} title="Next">›</button>
            <button className="page-btn" disabled={offset + limit >= total}
              onClick={() => setOffset((totalPages - 1) * limit)} title="Last">»</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
